/**
 * Central API endpoint definitions.
 * All backend paths live here; services and pages use these constants.
 * For dynamic segments use the helper functions (e.g. byId, bookingApprove).
 */

// Base path is applied by api instance (e.g. /api); paths below are relative to that.

export const ENDPOINTS = {
  HEALTH: '/health',

  // ─── Auth ─────────────────────────────────────────────────────────────────
  AUTH: {
    QR_LOGIN: '/auth/qr-login',
    SEND_OTP: '/sms/send',
    VERIFY_OTP: '/sms/verify',
    COMPLETE_PROFILE: '/auth/complete-profile',
    ADMIN_LOGIN: '/auth/admin-login',
    mirrorSession: (mirrorId, consume = false) =>
      `/auth/mirror/${mirrorId}/session?consume=${consume}`,
    startTvSession: (mirrorId) => `/auth/mirror/${mirrorId}/start-tv`,
    mirrorChallenge: (mirrorId) => `/auth/mirror/${mirrorId}/challenge`
  },

  // ─── Admin ───────────────────────────────────────────────────────────────
  ADMIN: {
    DASHBOARD_STATS: '/admin/dashboard/stats',
    CONTEXT: '/admin/context',
    
    BRANDS: '/admin/brands',
    BRANDS_ACTIVE: '/admin/brands/active',
    brandById: (id) => `/admin/brands/${id}`,
    brandStats: (id) => `/admin/brands/${id}/stats`,
    brandAdmin: (id) => `/admin/brands/${id}/admin`,
    
    SALONS: '/admin/salons',
    salonById: (id) => `/admin/salons/${id}`,
    salonAdmin: (id) => `/admin/salons/${id}/admin`,
    salonServices: (salonId) => `/admin/salons/${salonId}/services`,

    MIRRORS: '/admin/mirrors',
    MIRRORS_NEXT_ID: '/admin/mirrors/next-id',
    MIRRORS_NEXT_CODE: '/admin/mirrors/next-code',
    mirrorById: (id) => `/admin/mirrors/${id}`,
    mirrorServices: (mirrorId) => `/admin/mirrors/${mirrorId}/services`,
    mirrorFeaturePacks: (mirrorId) => `/admin/mirrors/${mirrorId}/feature-packs`,

    CATEGORIES: '/admin/categories',

    STYLISTS: '/admin/stylists',
    stylistById: (id) => `/admin/stylists/${id}`,

    SERVICES: '/admin/services',
    SERVICES_BULK_UPLOAD: '/admin/services/bulk-upload',
    SERVICES_BULK_ASSIGN_BRAND: '/admin/services/bulk-assign-brand',
    // MEDIA_UPLOAD: '/admin/media/upload',
    MEDIA_UPLOAD: '/upload/upload',

    FEATURE_PACKS: '/admin/feature-packs',
    featurePackById: (id) => `/admin/feature-packs/${id}`,
    FEEDS: '/admin/feeds',
    feedById: (id) => `/admin/feeds/${id}`,
    feedDuplicate: (id) => `/admin/feeds/${id}/duplicate`,
    PROMOTIONAL_FEEDS: '/admin/promotional-feeds',
    promotionalFeedById: (id) => `/admin/promotional-feeds/${id}`,
    promotionalFeedDuplicate: (id) => `/admin/promotional-feeds/${id}/duplicate`,
    ENGAGEMENT_SERVICES: '/admin/engagement-services',
    engagementServiceById: (id) => `/admin/engagement-services/${id}`,
    engagementServiceDuplicate: (id) => `/admin/engagement-services/${id}/duplicate`,

    USERS: '/admin/users',
    TRANSACTIONS: '/admin/transactions',
    ONGOING_SERVICES: '/admin/ongoing-services',
    ongoingServiceCancel: (bookingId) => `/admin/ongoing-services/${bookingId}/cancel`,
    REPORTS_CONSULTATIONS: '/admin/reports/consultations',
    FEEDBACK: '/admin/feedback',
    feedbackStatus: (feedbackId) => `/admin/feedback/${feedbackId}/status`,

    BOOKINGS_PENDING_APPROVAL: '/admin/bookings/pending-approval',
    bookingApprove: (bookingId) => `/admin/bookings/${bookingId}/approve`
  },

  // ─── Categories (public + admin CRUD) ────────────────────────────────────
  CATEGORIES: {
    LIST: '/categories',
    byId: (id) => `/categories/${id}`,
    duplicate: (id) => `/categories/${id}/duplicate`
  },

  // ─── Services (public list/detail + admin CRUD via /api/services) ─────────
  SERVICES: {
    LIST: '/services',
    byId: (id) => `/services/${id}`,
    duplicate: (id) => `/services/${id}/duplicate`
  },

  // ─── Account (web-only) ────────────────────────────────────────────────────
  ACCOUNT: {
    DEREGISTER: '/account/deregister',
  },

  // ─── Customer ────────────────────────────────────────────────────────────
  CUSTOMER: {
    DASHBOARD: '/customer/dashboard',
    SERVICES_CATEGORIES: '/customer/services/categories',
    mirrorDashboard: (mirrorId) => `/customer/mirror/${mirrorId}/dashboard`
  },

  // ─── Bookings ───────────────────────────────────────────────────────────
  BOOKINGS: {
    CREATE: '/bookings/create',
    HISTORY: '/bookings/history',
    byId: (bookingId) => `/bookings/${bookingId}`,
    start: (bookingId) => `/bookings/${bookingId}/start`,
    complete: (bookingId) => `/bookings/${bookingId}/complete`,
    photo: (bookingId) => `/bookings/${bookingId}/photo`,
    mirrorActive: (mirrorId) => `/bookings/mirror/${mirrorId}/active`,
    mirrorHistory: (mirrorId) => `/bookings/mirror/${mirrorId}/history`,
    createSession: (mirrorId) => `/bookings/mirror/${mirrorId}/create-session-booking`,
    startSession: (mirrorId, bookingId) =>
      `/bookings/mirror/${mirrorId}/${bookingId}/start-session`,
    updateSessionServices: (mirrorId, bookingId) =>
      `/bookings/mirror/${mirrorId}/${bookingId}/update-session-services`,
    completeSession: (mirrorId, bookingId) =>
      `/bookings/mirror/${mirrorId}/${bookingId}/complete-session`,
    photoSession: (mirrorId, bookingId) =>
      `/bookings/mirror/${mirrorId}/${bookingId}/photo-session`
  },

  // ─── Feedback ───────────────────────────────────────────────────────────
  FEEDBACK: {
    CREATE: '/feedback',
    fromMirror: (mirrorId) => `/feedback/mirror/${mirrorId}`
  },

  FEEDS: {
    LIST: '/feeds'
  },

  PROMOTIONAL_FEEDS: {
    LIST: '/promotional-feeds'
  },

  ENGAGEMENT_SERVICES: {
    LIST: '/engagement-services'
  },

  // ─── Payments ───────────────────────────────────────────────────────────
  PAYMENTS: {
    INITIATE: '/payments/initiate',
    VERIFY: '/payments/verify'
  },

  // ─── Mirrors (public helpers) ───────────────────────────────────────────
  MIRRORS: {
    resolve: (mirrorId) => `/mirrors/resolve/${mirrorId}`
  }
};

export default ENDPOINTS;
